import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import api from '../api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { PlusIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const Users = () => {
  const { canAccess, user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [filters, setFilters] = useState({ role: '', search: '' });

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue } = useForm();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await api.get(`/api/users?${params}`);
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (canAccess('users')) {
      fetchUsers();
    }
  }, [fetchUsers, canAccess]);

  

  const isSelf = (u) => {
    const authId = authUser?.id || authUser?._id;
    return authId && (u._id === authId);
  };

  const canEditUser = (u) => {
    if (isSelf(u)) return true;
    if (isAdmin) return true;
    if (authUser?.role === 'director') {
      return !['admin', 'director'].includes(u.role);
    }
    return false;
  };

  const canManageRoleFor = (u) => isAdmin && !isSelf(u);
  const canManageSalary = isAdmin || authUser?.role === 'director';

  const handleRoleChange = async (u, newRole) => {
    if (!isAdmin) {
      toast.error('Only admins can change user roles');
      return;
    }

    const userId = u._id;
    if (isSelf(u) && newRole !== 'admin') {
      toast.error("You can't change your own role to non-admin");
      return;
    }

    const confirmed = window.confirm(`Change role of ${u.name} to ${newRole}?`);
    if (!confirmed) return;
    try {
      await api.put(`/api/users/${userId}/role`, { role: newRole });
      toast.success('Role updated');
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to update role';
      toast.error(msg);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = { ...data };
      if (!isAdmin || modalType !== 'create') {
        delete payload.role;
      }

      if (modalType === 'create') {
        await api.post('/api/auth/register', payload);
        toast.success('User created successfully');
      } else {
        await api.put(`/api/users/${selectedUser._id}`, payload);
        toast.success('User updated successfully');
      }
      
      setShowModal(false);
      reset();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDeactivate = async (userId) => {
    if (window.confirm('Are you sure you want to deactivate this user?')) {
      try {
        await api.put(`/api/users/${userId}/deactivate`);
        toast.success('User deactivated successfully');
        fetchUsers();
      } catch (error) {
        toast.error('Failed to deactivate user');
      }
    }
  };

  const handleActivate = async (userId) => {
    try {
      await api.put(`/api/users/${userId}/activate`);
      toast.success('User activated successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const openModal = (type, user = null) => {
    setModalType(type);
    setSelectedUser(user);
    
    if (type === 'edit' && user) {
      setValue('name', user.name);
      setValue('email', user.email);
      setValue('employeeId', user.employeeId);
      setValue('role', user.role);
      setValue('profile.phone', user.profile?.phone || '');
      setValue('profile.designation', user.profile?.designation || '');
      setValue('profile.salary', user.profile?.salary || '');
      setValue('financials.totalAdvance', user.financials?.totalAdvance || 0);
      setValue('financials.totalLoan', user.financials?.totalLoan || 0);
    } else {
      reset();
    }
    
    setShowModal(true);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'director': return 'role-director';
      case 'accounts': return 'role-accounts';
      case 'user': return 'role-employee';
      case 'employee': return 'role-employee';
      default: return 'role-employee';
    }
  };

  if (!canAccess('users')) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600">Manage system users and their roles</p>
        </div>
        {canAccess('create_user') && (
          <button
            onClick={() => openModal('create')}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add User
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value })}
            className="input-field"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="director">Director</option>
            <option value="accounts">Accounts</option>
            <option value="user">User</option>
            <option value="employee">Employee</option>
          </select>
          
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="input-field"
            placeholder="Search users..."
          />
          
          <button
            onClick={() => setFilters({ role: '', search: '' })}
            className="btn-secondary"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <div className="spinner mx-auto"></div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">ID: {user.employeeId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {canManageRoleFor(user) ? (
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user, e.target.value)}
                          className="input-field w-36"
                        >
                          <option value="admin">Admin</option>
                          <option value="director">Director</option>
                          <option value="accounts">Accounts</option>
                          <option value="user">User</option>
                          <option value="employee">Employee</option>
                        </select>
                      ) : (
                        <span className={getRoleBadgeClass(user.role)}>
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{user.email}</div>
                      <div className="text-sm text-gray-500">{user.profile?.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => openModal('view', user)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      
                      {canEditUser(user) && (
                        <button
                          onClick={() => openModal('edit', user)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Edit user"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                      
                      {isAdmin && (
                        user.isActive ? (
                          <button
                            onClick={() => handleDeactivate(user._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivate(user._id)}
                            className="btn-success text-xs px-2 py-1"
                          >
                            Activate
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {modalType === 'create' && 'Add New User'}
                {modalType === 'edit' && 'Edit User'}
                {modalType === 'view' && 'User Details'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            {modalType === 'view' && selectedUser ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-500">Name:</span>
                    <span className="ml-2">{selectedUser.name}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Employee ID:</span>
                    <span className="ml-2">{selectedUser.employeeId}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Email:</span>
                    <span className="ml-2">{selectedUser.email}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Role:</span>
                    <span className={`ml-2 ${getRoleBadgeClass(selectedUser.role)}`}>
                      {selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Phone:</span>
                    <span className="ml-2">{selectedUser.profile?.phone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Designation:</span>
                    <span className="ml-2">{selectedUser.profile?.designation || 'N/A'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Full Name</label>
                    <input
                      {...register('name', { required: 'Name is required' })}
                      type="text"
                      className="input-field"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Employee ID</label>
                    <input
                      {...register('employeeId', { required: 'Employee ID is required' })}
                      type="text"
                      className="input-field"
                    />
                    {errors.employeeId && (
                      <p className="mt-1 text-sm text-red-600">{errors.employeeId.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Email</label>
                    <input
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Please enter a valid email'
                        }
                      })}
                      type="email"
                      className="input-field"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>

                  {modalType === 'create' && (
                    <div>
                      <label className="label">Password</label>
                      <input
                        {...register('password', { 
                          required: 'Password is required',
                          minLength: { value: 6, message: 'Password must be at least 6 characters' }
                        })}
                        type="password"
                        className="input-field"
                      />
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                      )}
                    </div>
                  )}

                  {isAdmin && modalType === 'create' && (
                    <div>
                      <label className="label">Role</label>
                      <select
                        {...register('role', { required: 'Role is required' })}
                        className="input-field"
                      >
                        <option value="">Select Role</option>
                        <option value="admin">Admin</option>
                        <option value="director">Director</option>
                        <option value="accounts">Accounts</option>
                        <option value="user">User</option>
                        <option value="employee">Employee</option>
                      </select>
                      {errors.role && (
                        <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="label">Phone</label>
                    <input
                      {...register('profile.phone')}
                      type="tel"
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="label">Designation</label>
                    <input
                      {...register('profile.designation')}
                      type="text"
                      className="input-field"
                    />
                  </div>

                  {canManageSalary && (
                    <>
                      <div>
                        <label className="label">Salary</label>
                        <input
                          {...register('profile.salary')}
                          type="number"
                          step="0.01"
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="label">Outstanding Advance</label>
                        <input
                          {...register('financials.totalAdvance')}
                          type="number"
                          min="0"
                          step="0.01"
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="label">Outstanding Loan</label>
                        <input
                          {...register('financials.totalLoan')}
                          type="number"
                          min="0"
                          step="0.01"
                          className="input-field"
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary"
                  >
                    {isSubmitting ? 'Saving...' : (modalType === 'create' ? 'Create User' : 'Update User')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
